import React, { useState, useCallback, useEffect, useRef } from "react";
import _ from "underscore";
import styles from "../prediction/prediction.module.css";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";
import TargetIcon from "../../../assets/icons/target.svg";
import SearchIcon from "../../../assets/icons/search-icon.svg";
import CircleInfoIcon from "../../../assets/icons/circle-info.svg";
import SettingsIcon from "../../../assets/icons/settings.svg";
import UpdateIcon from "../../../assets/icons/update-icon.svg";
import FileExportIcon from "../../../assets/icons/file-export.svg";
import InfoIcon from "../../../assets/icons/info-icon.svg";
import { dropoutRiskResults, dropoutRiskHistory as fetchDropoutRiskHistoryApi, dropoutRiskDashboard as fetchDropoutRiskDashboardApi } from "../../../services/prediction";
import { useInstitution } from "../../../context/InstitutionContext";
import { toast } from "react-toastify";
import loadingGif from "../../../assets/loading.gif";
import api from "../../../http/api";
import { Doughnut } from "react-chartjs-2";
import { usePdfExport } from "../../../hooks/usePdfExport";
import {
  chanceTooltip,
  describeCalibration,
  describeMissingCalibration,
  formatChance,
  horizonLabel,
  isRetroEstimate,
  probabilityForStudent
} from "../../../utils/dropoutProbability";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";

ChartJS.register(ArcElement, ChartTooltip, Legend);

const DropoutRisk = () => {
  const { institution } = useInstitution();

  const [searchTerm1, setSearchTerm1] = useState("");
  const [predictionData, setPredictionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStudentReasons, setSelectedStudentReasons] = useState(null);

  const [currentPage1, setCurrentPage1] = useState(1);
  const [sortConfig, setSortConfig] = useState(null);
  const [itemsPerPage] = useState(15);
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [bandRates, setBandRates] = useState(null);
  const [probabilityStatus, setProbabilityStatus] = useState(null);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [predictionStatus, setPredictionStatus] = useState(null);

  const [generating, setGenerating] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);

  const chartsRef = useRef(null);
  const { exportPdf, exportingPdf } = usePdfExport();

  const formatCurrency = (value) => {
    return `R$ ${Number(value).toFixed(2).replace('.', ',')}`;
  };

  const formatExecutionDate = (dateString) => {
    if (!dateString) return '-';
    const normalized = dateString.endsWith('Z') || dateString.includes('+') ? dateString : dateString + 'Z';
    const date = new Date(normalized);
    return date.toLocaleString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const buildDropoutFactors = (student) => {
    let reasons = [];
    try {
      reasons = student.reasonsJson ? JSON.parse(student.reasonsJson) : [];
    } catch { reasons = []; }

    return reasons.map(reason => {
      if (reason.includes('Frequência')) {
        // Texto novo: "abaixo do mínimo de 75%" ou "crítica (..., abaixo de 50%)";
        // texto legado: "abaixo de 50%". O valor do aluno vem do próprio registro.
        const isCritical = reason.includes('crítica') || !reason.includes('75');
        const limit = isCritical ? 50 : 75;
        const studentVal = student.avgFrequency != null
          ? Number(student.avgFrequency).toFixed(1) + '%'
          : '-';
        let comparisonText = null;
        if (student.avgFrequency != null) {
          const diff = Math.abs(((student.avgFrequency - limit) / limit) * 100).toFixed(0);
          comparisonText = `${diff}% abaixo do limite`;
        }
        return {
          friendlyName: isCritical ? 'Frequência crítica' : 'Frequência abaixo do mínimo',
          description: reason,
          value: studentVal,
          formattedAvg: `${limit}%`,
          avgLabel: 'Limite mínimo',
          comparisonText,
        };
      }

      if (reason.includes('limiar de aprovação')) {
        const thrMatch = reason.match(/limiar de aprovação \(([\d.,]+)\)/);
        return {
          friendlyName: 'Nota abaixo do limiar de aprovação',
          description: reason,
          value: student.avgFinalGrade != null ? Number(student.avgFinalGrade).toFixed(1) : '-',
          formattedAvg: thrMatch ? thrMatch[1] : '-',
          avgLabel: 'Limiar de aprovação',
          comparisonText: null,
        };
      }

      if (reason.includes('virar inadimplente')) {
        const probMatch = reason.match(/\(([\d.,]+)\s*%\)/);
        return {
          friendlyName: 'Risco de inadimplência previsto',
          description: reason,
          value: probMatch ? `${probMatch[1]}%` : '-',
          formattedAvg: null,
          avgLabel: null,
          comparisonText: 'Previsão da análise preditiva de inadimplência (próximos 30 dias)',
        };
      }

      if (reason.includes('Sem dados acadêmicos')) {
        return {
          friendlyName: 'Sem dados acadêmicos no período',
          description: reason,
          value: 'Avaliado pelo financeiro',
          formattedAvg: null,
          avgLabel: null,
          comparisonText: null,
        };
      }

      if (reason.includes('Nota') && reason.includes('abaixo da média')) {
        const studentVal = student.avgFinalGrade != null ? Number(student.avgFinalGrade).toFixed(1) : '-';
        const avgVal = student.institutionAvgGrade != null ? Number(student.institutionAvgGrade).toFixed(1) : '-';
        let comparisonText = null;
        if (student.avgFinalGrade != null && student.institutionAvgGrade != null && student.institutionAvgGrade > 0) {
          const diff = Math.abs(((student.avgFinalGrade - student.institutionAvgGrade) / student.institutionAvgGrade) * 100).toFixed(0);
          comparisonText = `${diff}% abaixo da média`;
        }
        return {
          friendlyName: 'Nota abaixo da média',
          description: reason,
          value: studentVal,
          formattedAvg: avgVal,
          avgLabel: 'Média da instituição',
          comparisonText,
        };
      }

      if (reason.includes('Nota final inexistente')) {
        return {
          friendlyName: 'Nota final inexistente',
          description: reason,
          value: 'Sem registro',
          formattedAvg: student.institutionAvgGrade != null ? Number(student.institutionAvgGrade).toFixed(1) : '-',
          avgLabel: 'Média da instituição',
          comparisonText: null,
        };
      }

      if (reason.includes('Inadimplente')) {
        const parcelas = student.overdueInstallments != null ? student.overdueInstallments : '-';
        const valor = student.overdueValue != null ? `R$ ${Number(student.overdueValue).toFixed(2).replace('.', ',')}` : '-';
        return {
          friendlyName: 'Inadimplência financeira',
          description: reason,
          value: `${parcelas} parcela${parcelas !== 1 ? 's' : ''}`,
          formattedAvg: valor,
          avgLabel: 'Valor em atraso',
          comparisonText: null,
        };
      }

      return {
        friendlyName: reason,
        description: reason,
        value: '-',
        formattedAvg: null,
        avgLabel: null,
        comparisonText: null,
      };
    });
  };

  const handleOpenModal = (student) => {
    const factors = buildDropoutFactors(student);
    // motivos crus, exatamente como a análise gravou (o parse acima pode não reconhecer
    // textos de versões antigas das regras — aqui nada se perde)
    let rawReasons = [];
    try {
      rawReasons = student.reasonsJson ? JSON.parse(student.reasonsJson) : [];
    } catch { rawReasons = []; }
    // student inteiro: o modal também mostra os dados que saíram da tabela
    setSelectedStudentReasons({ studentId: student.studentId, factors, rawReasons, student });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedStudentReasons(null);
  };

  const handleOpenInfoModal = () => setInfoModalOpen(true);
  const handleCloseInfoModal = () => setInfoModalOpen(false);

  const handleGenerateAnalysis = async () => {
    if (generating) return;

    try {
      setGenerating(true);
      toast.info("Iniciando análise de evasão...");

      const response = await api.client.post(
        'api/v1/DropoutRiskStudents/run',
        null,
        { headers: { institutionId: institution.id } }
      );

      if (response.data?.success) {
        toast.success(response.data.message || "Análise de evasão concluída!");
        fetchDropoutData(1, "");
        fetchDashboard();
      }
    } catch (error) {
      const data = error?.response?.data;
      toast.error(data?.message || "Erro ao executar a análise de evasão. Tente novamente.");
    } finally {
      setGenerating(false);
    }
  };

  const fetchDropoutStatus = useCallback(async () => {
    try {
      const response = await fetchDropoutRiskHistoryApi({
        institutionId: institution.id,
        pageNumber: 1,
        pageSize: 1
      });

      const history = response?.dropoutRiskHistory?.data;
      if (history && history.length > 0) {
        const latest = history[0];
        setPredictionStatus({
          status: latest.status,
          errorMessage: latest.errorMessage,
          endedAt: latest.endedAt,
          studentsAnalyzed: latest.studentsAnalyzed,
          studentsClassified: latest.studentsClassified,
        });
      } else {
        setPredictionStatus({ status: "never-run" });
      }
    } catch {
      setPredictionStatus(null);
    }
  }, [institution.id]);

  const fetchDropoutData = useCallback((pageNumber = 1, searchTerm = "") => {
    const debouncedFetch = _.debounce(async () => {
      try {
        setLoading(true);
        const response = await dropoutRiskResults({
          institutionId: institution.id,
          pageNumber: pageNumber,
          pageSize: itemsPerPage
        });

        const result = response?.dropoutRiskResults;
        if (result) {
          let data = result.data || [];
          if (searchTerm) {
            data = data.filter(s => String(s.studentId).includes(searchTerm));
          }
          setPredictionData(data);
          setPaginationInfo(result.pagination);
          setBandRates(result.bandRates || null);
          setProbabilityStatus(result.probabilityStatus || null);
        }

        if (!result?.data || result.data.length === 0) {
          fetchDropoutStatus();
        }
      } catch (error) {
        console.error("Erro ao buscar dados de evasão:", error);
        fetchDropoutStatus();
      } finally {
        setLoading(false);
      }
    }, 1000);

    debouncedFetch();
  }, [institution.id, itemsPerPage, fetchDropoutStatus]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetchDropoutRiskDashboardApi({ institutionId: institution.id });
      setDashboardData(res?.dropoutRiskDashboard || null);
    } catch (error) {
      console.error("Erro ao buscar dashboard de evasão:", error);
    }
  }, [institution.id]);

  const handleSearch1 = (term) => {
    setSearchTerm1(term);
    setCurrentPage1(1);
    fetchDropoutData(1, term);
  };

  const handlePageClick1 = (page) => {
    setCurrentPage1(page);
    fetchDropoutData(page, searchTerm1);
  };

  const handleExportCsv = async () => {
    if (!institution?.id || !predictionData || predictionData.length === 0) {
      toast.error("Nenhum dado disponível para exportação");
      return;
    }

    try {
      setExporting(true);
      toast.info("Preparando exportação...");

      const headers = ["Id do Aluno", "Score", "Faixa de Risco", "Prob. de evasão (histórica)", "Frequência", "Nota Final", "Nota Intermediária", "Nota Prova Final", "Média Instituição", "Parcelas Vencidas", "Valor em Atraso", "Motivos", "Analisado em"];
      const rows = predictionData.map(s => {
        let reasons = [];
        try { reasons = s.reasonsJson ? JSON.parse(s.reasonsJson) : []; } catch { reasons = []; }
        return [
          s.studentId,
          s.matchedCriteriaCount ?? "",
          s.riskBand || "",
          rateForStudent(s) || "",
          s.avgFrequency != null ? Number(s.avgFrequency).toFixed(1) + "%" : "",
          s.avgFinalGrade != null ? Number(s.avgFinalGrade).toFixed(1) : "",
          s.avgIntermediateGrade != null ? Number(s.avgIntermediateGrade).toFixed(1) : "",
          s.avgFinalExamGrade != null ? Number(s.avgFinalExamGrade).toFixed(1) : "",
          s.institutionAvgGrade != null ? Number(s.institutionAvgGrade).toFixed(1) : "",
          s.overdueInstallments ?? "",
          s.overdueValue != null ? Number(s.overdueValue).toFixed(2) : "",
          reasons.join("; "),
          s.analyzedAt || ""
        ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(";");
      });
      const csv = [headers.map(h => `"${h}"`).join(";"), ...rows].join("\n");
      const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `evasao_${institution.id}_${new Date().toISOString().split("T")[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("CSV exportado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar CSV:", error);
      toast.error("Erro ao exportar dados. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!institution?.id || !predictionData || predictionData.length === 0) {
      toast.error("Nenhum dado disponível para exportação");
      return;
    }

    try {
      toast.info("Preparando PDF... Buscando todos os registros.");

      const totalRecords = paginationInfo?.totalCount || predictionData.length;
      const allDataResponse = await dropoutRiskResults({
        institutionId: institution.id,
        pageNumber: 1,
        pageSize: totalRecords,
      });

      const allData = allDataResponse?.dropoutRiskResults?.data || predictionData;

      const tableColumns = [
        "Id do Aluno", "Frequência", "Nota Final",
        "Nota Intermediária", "Parcelas Vencidas", "Valor em Atraso",
        "Analisado em", "Motivos"
      ];

      const formatReasonsForPdf = (reasonsJson) => {
        if (!reasonsJson) return "-";
        try {
          const reasons = JSON.parse(reasonsJson);
          if (!Array.isArray(reasons) || reasons.length === 0) return "-";
          return reasons.join(", ");
        } catch { return "-"; }
      };

      const tableRows = allData
        .sort((a, b) => (b.matchedCriteriaCount || 0) - (a.matchedCriteriaCount || 0))
        .map(item => [
          String(item.studentId),
          item.avgFrequency != null ? `${Number(item.avgFrequency).toFixed(1)}%` : "-",
          item.avgFinalGrade != null ? Number(item.avgFinalGrade).toFixed(1) : "-",
          item.avgIntermediateGrade != null ? Number(item.avgIntermediateGrade).toFixed(1) : "-",
          item.overdueInstallments != null ? String(item.overdueInstallments) : "-",
          item.overdueValue != null ? formatCurrency(item.overdueValue) : "-",
          item.analyzedAt ? formatExecutionDate(item.analyzedAt) : "-",
          formatReasonsForPdf(item.reasonsJson),
        ]);

      const filename = `evasao_${institution.id}_${new Date().toISOString().split("T")[0]}.pdf`;
      await exportPdf([chartsRef], {
        title: "Análise Preditiva de Evasão",
        filename,
        tableData: { columns: tableColumns, rows: tableRows },
      });
      toast.success("PDF exportado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar PDF:", error);
      toast.error("Erro ao exportar PDF. Tente novamente.");
    }
  };

  const getEmptyStateInfo = () => {
    if (!predictionStatus) {
      return {
        title: "Nenhuma análise de evasão encontrada",
        description: "Não há dados de evasão disponíveis no momento.",
        type: "default"
      };
    }

    if (predictionStatus.status === "never-run") {
      return {
        title: "Análise de evasão ainda não foi executada",
        description: "A análise de evasão ainda não foi executada para esta instituição. Você pode gerar a primeira análise agora ou aguardar a execução automática semanal.",
        type: "info"
      };
    }

    if (predictionStatus.status === "skipped") {
      return {
        title: "Análise não executada: sem alunos ativos",
        description: "A instituição não possui alunos ativos, então a análise de evasão não foi executada. Importe o arquivo de Alunos (com o status \"Ativo\") para habilitar a análise.",
        type: "info"
      };
    }

    if (predictionStatus.status === "failed") {
      return {
        title: "A última análise de evasão falhou",
        description: predictionStatus.errorMessage
          ? `Erro: ${predictionStatus.errorMessage.substring(0, 200)}`
          : "Ocorreu um erro durante a execução. A equipe técnica foi notificada.",
        type: "error"
      };
    }

    return {
      title: "Nenhuma análise de evasão encontrada",
      description: "Não há dados de evasão disponíveis no momento. Verifique se os arquivos de Alunos, Acadêmico e Faturamento foram importados corretamente.",
      type: "default"
    };
  };

  useEffect(() => {
    if (institution?.id) {
      fetchDropoutData(1, "");
      fetchDashboard();
    }
  }, [institution?.id, fetchDropoutData, fetchDashboard]);

  useEffect(() => {
    if (infoModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [infoModalOpen]);

  // Colunas ordenáveis da tabela (as colunas "Prob. de evasão" — derivada do score —
  // e "Motivos" — só botão — ficam de fora). Frequência/notas/financeiro saíram da
  // tabela e aparecem no modal de motivos.
  const sortableColumns = [
    { label: "Id do Aluno", key: "studentId" },
    { label: "Analisado em", key: "analyzedAt" },
    { label: "Score", key: "matchedCriteriaCount" },
  ];

  // Bandas em minúsculo vêm do backend (cortes 0-10: alto >= 6, medio 3-5)
  const RISK_BAND_DOT = { alto: "bandDotAlto", medio: "bandDotMedio", baixo: "bandDotBaixo" };
  const RISK_BAND_TEXT = { alto: "bandAlto", medio: "bandMedio", baixo: "bandBaixo" };

  // Chance de evadir: probabilidade calibrada do score (o backend resolve a curva em byScore).
  const rateForStudent = (student) => formatChance(probabilityForStudent(bandRates, student));

  // Ciclo por clique: asc → desc → ordem padrão (por risco).
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return prev.dir === "asc" ? { key, dir: "desc" } : null;
      }
      return { key, dir: "asc" };
    });
  };

  // ponytail: ordena só a página carregada; sort server-side se precisar ordenar o total.
  const currentTableData1 = predictionData
    ? [...predictionData].sort((a, b) => {
        if (!sortConfig) {
          return (b.matchedCriteriaCount || 0) - (a.matchedCriteriaCount || 0);
        }
        const va = a[sortConfig.key];
        const vb = b[sortConfig.key];
        if (va == null && vb == null) {
          return 0;
        }
        if (va == null) {
          return 1;
        }
        if (vb == null) {
          return -1;
        }
        const na = Number(va);
        const nb = Number(vb);
        const cmp = !Number.isNaN(na) && !Number.isNaN(nb)
          ? na - nb
          : String(va).localeCompare(String(vb));
        return sortConfig.dir === "asc" ? cmp : -cmp;
      })
    : [];

  return (
    <div className={styles.predictionContent}>
      {!infoModalOpen && (
        <>
          {/* Gráficos: evasores por curso + critérios + indicador */}
          {dashboardData && (() => {
            const courseData = dashboardData.dropoutByCourse || [];
            const breakdown = dashboardData.criteriaBreakdown || [];
            const hasCourseData = courseData.length > 0;
            const hasCriteria = breakdown.length > 0;

            if (!hasCourseData && !hasCriteria) return null;

            const courseColors = ['#0962AD', '#DA5252', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];

            const coursePieData = hasCourseData ? {
              labels: courseData.map(c => c.courseName),
              datasets: [{
                data: courseData.map(c => c.studentCount),
                backgroundColor: courseData.map((_, idx) => courseColors[idx % courseColors.length]),
                borderColor: '#ffffff',
                borderWidth: 2,
              }],
            } : null;

            const criteriaColors = ['#DA5252', '#f59e0b', '#0962AD', '#10b981', '#8b5cf6'];

            const criteriaPieData = hasCriteria ? {
              labels: breakdown.map(b => b.criteria),
              datasets: [{
                data: breakdown.map(b => b.count),
                backgroundColor: breakdown.map((_, idx) => criteriaColors[idx % criteriaColors.length]),
                borderColor: '#ffffff',
                borderWidth: 2,
              }],
            } : null;

            const doughnutOptions = (colors) => ({
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: {
                    usePointStyle: true,
                    boxHeight: 6,
                    color: '#64748b',
                    font: { size: 12 },
                    padding: 12,
                    generateLabels: (chart) => {
                      const data = chart.data;
                      const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                      return data.labels.map((label, i) => {
                        const value = data.datasets[0].data[i];
                        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                        return {
                          text: `${label} (${pct}%)`,
                          fillStyle: data.datasets[0].backgroundColor[i],
                          strokeStyle: data.datasets[0].borderColor,
                          lineWidth: data.datasets[0].borderWidth,
                          pointStyle: 'circle',
                          hidden: !chart.getDataVisibility(i),
                          index: i,
                        };
                      });
                    },
                  },
                },
                tooltip: {
                  callbacks: {
                    label: (ctx) => {
                      const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                      const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : '0';
                      return `${ctx.label}: ${ctx.parsed} aluno${ctx.parsed !== 1 ? 's' : ''} (${pct}%)`;
                    },
                  },
                },
              },
            });

            return (
              <div className={styles.courseChartsRow} ref={chartsRef}>
                {hasCourseData && (
                  <div className={styles.tableContainer}>
                    <HeaderTitle title="Possíveis evasores por curso" icon={TargetIcon} />
                    <span className={styles.chartSubtitle}>Distribuição dos alunos em risco de evasão por curso</span>
                    <div className={styles.courseChartWrapper}>
                      <Doughnut data={coursePieData} options={doughnutOptions(courseColors)} />
                    </div>
                  </div>
                )}

                {hasCriteria && (
                  <div className={styles.tableContainer}>
                    <HeaderTitle title="Possíveis evasores por critério" icon={TargetIcon} />
                    <span className={styles.chartSubtitle}>Distribuição dos critérios de risco entre os alunos classificados</span>
                    <div className={styles.courseChartWrapper}>
                      <Doughnut data={criteriaPieData} options={doughnutOptions(criteriaColors)} />
                    </div>
                  </div>
                )}

              </div>
            );
          })()}

          {/* Primeira Tabela - Análise Preditiva de Evasão */}
          <div className={styles.tableContainer}>
            <div className={styles.headerTitleWithActions}>
              <div className={styles.headerTitleGroup}>
                <HeaderTitle title="Análise Preditiva de Evasão" icon={TargetIcon} />
                <button
                  className={styles.infoIconButton}
                  onClick={handleOpenInfoModal}
                  title="Como funciona a análise de evasão?"
                >
                  <img src={CircleInfoIcon} alt="Informações" className={styles.infoIcon} />
                </button>
              </div>
              {predictionData && predictionData.length > 0 && !loading && (
                <div className={styles.headerRight}>
                  <div className={styles.searchWrapper}>
                    <img src={SearchIcon} alt="Pesquisar" className={styles.searchIcon} />
                    <input
                      type="text"
                      placeholder="Pesquisar..."
                      value={searchTerm1}
                      onChange={(e) => handleSearch1(e.target.value)}
                      className={styles.searchInput}
                    />
                  </div>
                  <button
                    className={styles.iconOnlyButton}
                    onClick={() => fetchDropoutData(currentPage1, searchTerm1)}
                    disabled={loading}
                    title="Recarregar lista (não executa nova análise)"
                    aria-label="Recarregar lista"
                  >
                    {loading ? (
                      <img src={loadingGif} alt="Carregando" className={styles.iconOnlyButtonIcon} />
                    ) : (
                      <img src={UpdateIcon} alt="" className={styles.iconOnlyButtonIcon} />
                    )}
                  </button>
                  <button
                    className={styles.updateButton}
                    onClick={handleGenerateAnalysis}
                    disabled={generating}
                    title="Reprocessa os alunos e refaz os estudos retroativos pendentes"
                  >
                    {generating ? (
                      <img src={loadingGif} alt="Executando" className={styles.loadingIcon} />
                    ) : (
                      <img src={SettingsIcon} alt="Executar" className={styles.updateButtonIcon} />
                    )}
                    {generating ? "Executando análise..." : "Executar nova análise"}
                  </button>
                  <button
                    className={styles.exportButton}
                    onClick={handleExportCsv}
                    disabled={exporting}
                    title="Exportar CSV"
                  >
                    {exporting ? (
                      <img src={loadingGif} alt="Exportando..." className={styles.exportIcon} />
                    ) : (
                      <img src={FileExportIcon} alt="Exportar" className={styles.exportIcon} />
                    )}
                    {exporting ? "Exportando..." : "Exportar CSV"}
                  </button>
                  <button
                    className={styles.exportButton}
                    onClick={handleExportPdf}
                    disabled={exportingPdf}
                    title="Exportar PDF"
                  >
                    {exportingPdf ? (
                      <img src={loadingGif} alt="Exportando..." className={styles.exportIcon} />
                    ) : (
                      <img src={FileExportIcon} alt="Exportar" className={styles.exportIcon} />
                    )}
                    {exportingPdf ? "Exportando..." : "Exportar PDF"}
                  </button>
                </div>
              )}
            </div>

        {(!predictionData || predictionData.length === 0) && !loading ? (() => {
          const emptyInfo = getEmptyStateInfo();
          return (
            <div className={`${styles.emptyState} ${styles[`emptyState_${emptyInfo.type}`] || ''}`}>
              <div className={styles.emptyStateIcon}>
                <img src={CircleInfoIcon} alt="Informação" />
              </div>
              <div className={styles.emptyStateContent}>
                <h2 className={styles.emptyStateTitle}>
                  {emptyInfo.title}
                </h2>
                <p className={styles.emptyStateDescription}>
                  {emptyInfo.description}
                </p>
                {emptyInfo.type === "warning" && (
                  <a href="/app/importar-dados" className={styles.importDataLink}>
                    Ir para Importar Dados
                  </a>
                )}
                <button
                  className={styles.generateButton}
                  onClick={handleGenerateAnalysis}
                  disabled={generating}
                >
                  {generating ? (
                    <img src={loadingGif} alt="Gerando..." className={styles.generateButtonIcon} />
                  ) : (
                    <img src={SettingsIcon} alt="Configurações" className={styles.generateButtonIcon} />
                  )}
                  {generating ? "Executando análise..." : "Executar análise de evasão"}
                </button>
              </div>
            </div>
          );
        })() : (
          <div>
            <div className={styles.horizonBanner}>
              <div className={styles.horizonBannerIcon}>
                <img src={TargetIcon} alt="Evasão" />
              </div>
              <div className={styles.horizonBannerText}>
                <strong>Análise baseada em regras</strong>
                <span>
                  Os alunos listados estão ativos e atendem a <strong>pelo menos um</strong> critério de risco:
                  <strong> frequência abaixo de 75%</strong> (crítica abaixo de 50%), <strong>nota abaixo do limiar de aprovação</strong>,
                  <strong> parcelas vencidas</strong> e/ou <strong>risco previsto de inadimplência</strong>.
                  Cada critério soma pontos de severidade e a lista é ordenada do maior risco para o menor —
                  os motivos de cada aluno aparecem no ícone de detalhes. Variáveis sem dados são ignoradas na avaliação.
                </span>
              </div>
            </div>

            {/* Já existe ciclo medido, mas sem curva: explica o motivo e mostra a taxa média */}
            {!bandRates && probabilityStatus && (
              <p className={styles.subtleNotice}>{describeMissingCalibration(probabilityStatus)}</p>
            )}

            {/* Some quando existir curva calibrada (backtest maduro ou estudo retroativo) */}
            {!bandRates && (
              <p className={styles.subtleNotice}>
                💡 Importe o histórico de status de semestres anteriores em{" "}
                <a href="/app/importar-dados" className={styles.infoLink}>Importar Dados</a>{" "}
                (<a href="/model-files/historico_status.xlsx" download className={styles.infoLink}>baixar arquivo de exemplo</a>) —
                com dados retroativos a análise fica mais precisa e a probabilidade de evasão de cada aluno aparece
                sem esperar o ciclo de validação de um semestre.
              </p>
            )}

            <table className={styles.predictionTable}>
              <thead>
                <tr>
                  {sortableColumns.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      style={{ cursor: "pointer", userSelect: "none" }}
                      title="Clique para ordenar"
                    >
                      {col.label}{" "}
                      {sortConfig?.key === col.key ? (
                        <span>{sortConfig.dir === "asc" ? "▲" : "▼"}</span>
                      ) : (
                        <span style={{ opacity: 0.35 }}>⇅</span>
                      )}
                    </th>
                  ))}
                  {/* Coluna só aparece com backtest maduro ou estudo retroativo (snapshot importado) */}
                  {bandRates && (
                    <th
                      title={`${describeCalibration(bandRates)} Passe o mouse sobre o percentual para ver o intervalo.`}
                    >
                      Prob. de evasão em {horizonLabel(bandRates.horizonDays)}{isRetroEstimate(bandRates) ? " *" : ""}
                    </th>
                  )}
                  <th>Motivos da Evasão</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={bandRates ? 5 : 4} style={{ textAlign: 'center', padding: '40px' }}>
                      <img src={loadingGif} alt="Carregando..." className={styles.loadingIcon} />
                      <p>Carregando dados de evasão...</p>
                    </td>
                  </tr>
                ) : currentTableData1.length > 0 ? (
                  currentTableData1.map((student, idx) => (
                      <tr key={student.id || idx}>
                        <td>{student.studentId}</td>
                        <td>{student.analyzedAt ? formatExecutionDate(student.analyzedAt) : '-'}</td>
                        <td>
                          <span className={`${styles.bandIndicator} ${styles[RISK_BAND_TEXT[student.riskBand]] || ''}`}>
                            {student.matchedCriteriaCount}
                            <span className={`${styles.bandDot} ${styles[RISK_BAND_DOT[student.riskBand]] || ''}`}></span>
                            {student.riskBand || '-'}
                          </span>
                        </td>
                        {bandRates && (
                          <td title={chanceTooltip(bandRates, student)}>{rateForStudent(student) ?? '—'}</td>
                        )}
                        <td>
                          {student.reasonsJson ? (
                            <button
                              className={styles.factorsButton}
                              onClick={() => handleOpenModal(student)}
                              title="Ver detalhes dos motivos de evasão"
                            >
                              <img src={InfoIcon} alt="Ver detalhes" className={styles.factorsIcon} />
                            </button>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={bandRates ? 5 : 4} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Nenhum dado de evasão encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {predictionData && predictionData.length > 0 && (
              <div className={styles.pagination}>
                <button
                  onClick={() => handlePageClick1(currentPage1 - 1)}
                  disabled={!paginationInfo?.hasPreviousPage || currentPage1 <= 1}
                  className={styles.paginationButton}
                >
                  Anterior
                </button>

                {paginationInfo?.totalPages > 1 && (() => {
                  const totalPages = paginationInfo.totalPages;
                  const currentPage = currentPage1;
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);

                  if (endPage - startPage < 4) {
                    startPage = Math.max(1, endPage - 4);
                  }

                  const pages = [];

                  if (startPage > 1) {
                    pages.push(
                      <button key={1} onClick={() => handlePageClick1(1)}>1</button>
                    );
                    if (startPage > 2) {
                      pages.push(<span key="ellipsis1">...</span>);
                    }
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        className={i === currentPage ? styles.activePage : ""}
                        onClick={() => handlePageClick1(i)}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(<span key="ellipsis2">...</span>);
                    }
                    pages.push(
                      <button key={totalPages} onClick={() => handlePageClick1(totalPages)}>
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}

                <button
                  onClick={() => handlePageClick1(currentPage1 + 1)}
                  disabled={!paginationInfo?.hasNextPage || currentPage1 >= (paginationInfo?.totalPages || 1)}
                  className={styles.paginationButton}
                >
                  Próximo
                </button>

                <span className={styles.paginationInfo}>
                  Página {paginationInfo?.pageNumber || currentPage1} de {paginationInfo?.totalPages || 1}
                  ({paginationInfo?.totalCount || predictionData?.length || 0} itens)
                </span>
              </div>
            )}
          </div>
        )}
      </div>
        </>
      )}

      {/* Tela de Informações sobre a Análise de Evasão */}
      {infoModalOpen && (
        <div className={styles.fullScreenOverlay}>
          <div className={styles.fullScreenContent}>
            <div className={styles.fullScreenHeader}>
              <h2 className={styles.fullScreenTitle}>
                Como funciona a Análise Preditiva de Evasão?
              </h2>
              <button className={styles.fullScreenCloseButton} onClick={handleCloseInfoModal}>
                ×
              </button>
            </div>
            
            <div className={styles.fullScreenBody}>
              <div className={styles.infoSection}>
                <p className={styles.infoDescription}>
                  A análise de evasão identifica alunos ativos com <strong>risco de abandonar</strong> a instituição,
                  com base em critérios acadêmicos e financeiros. O sistema avalia automaticamente frequência,
                  notas e situação de pagamento, classificando os alunos que atendem a pelo menos um dos critérios de risco.
                </p>
                <p className={styles.infoDescription}>
                  Essa análise permite que a instituição atue de forma <strong>proativa</strong>, oferecendo suporte
                  e acompanhamento personalizado antes que a evasão aconteça.
                </p>
              </div>

              <div className={styles.howItWorksSection}>
                <h3 className={styles.howItWorksTitle}>Como o Algoritmo Funciona?</h3>
                
                <div className={styles.howItWorksStep}>
                  <h4 className={styles.stepTitle}>1. Importação de Dados</h4>
                  <p className={styles.stepDescription}>
                    Você só precisa importar os arquivos da instituição na tela de <a href="/app/importar-dados" className={styles.infoLink}>Importar Dados</a> — principalmente <strong className={styles.highlightFile}>Faturamento</strong>, <strong className={styles.highlightFile}>Acadêmico</strong>, <strong className={styles.highlightFile}>Turmas</strong> e <strong className={styles.highlightFile}>Alunos</strong>. A partir daí, a plataforma faz todo o resto de forma automática.
                  </p>
                </div>

                <div className={styles.howItWorksStep}>
                  <h4 className={styles.stepTitle}>2. Frequência abaixo do mínimo</h4>
                  <p className={styles.stepDescription}>
                    Alunos com frequência média abaixo do mínimo legal de <strong>75%</strong> somam 3 pontos de risco;
                    abaixo de <strong>50%</strong> (frequência crítica), 4 pontos — o critério de maior peso.
                    Sem dado de frequência, o critério é ignorado.
                  </p>
                </div>

                <div className={styles.howItWorksStep}>
                  <h4 className={styles.stepTitle}>3. Nota abaixo do limiar de aprovação</h4>
                  <p className={styles.stepDescription}>
                    A nota final média é comparada com o <strong>limiar de aprovação</strong> do curso (quando informado)
                    ou com um limiar inferido pela escala de notas da instituição (6 em escala 0–10; 60 em escala 0–100).
                    Nota abaixo do limiar soma 3 pontos. Ausência de nota <strong>não</strong> sinaliza o aluno.
                  </p>
                </div>

                <div className={styles.howItWorksStep}>
                  <h4 className={styles.stepTitle}>4. Situação financeira</h4>
                  <p className={styles.stepDescription}>
                    Alunos com <strong>parcelas vencidas</strong> somam 2 pontos (1–2 parcelas) ou 3 pontos (3 ou mais).
                    Quem está em dia é avaliado pela <strong>análise preditiva de inadimplência</strong>: risco alto de
                    atraso nos próximos 30 dias soma 3 pontos; risco médio, 2 pontos.
                  </p>
                </div>

                <div className={styles.howItWorksStep}>
                  <h4 className={styles.stepTitle}>5. Execução automática</h4>
                  <p className={styles.stepDescription}>
                    A análise é executada automaticamente toda <strong>segunda-feira às 2:30 (horário de Brasília)</strong> e
                    os resultados são persistidos. Também é possível executá-la manualmente pelo botão "Executar análise de evasão".
                  </p>
                </div>
              </div>

              <div className={styles.variablesSection}>
                <h3 className={styles.variablesTitle}>Variáveis Consideradas pelo Algoritmo</h3>
                <p className={styles.variablesIntro}>
                  A análise utiliza os seguintes dados para avaliar o risco de evasão de cada aluno:
                </p>

                <div className={styles.variableCategory}>
                  <h4 className={styles.variableCategoryTitle}>📚 Dados Acadêmicos</h4>
                  <ul className={styles.variableList}>
                    <li><strong>Frequência média</strong> — percentual de presença nas disciplinas</li>
                    <li><strong>Nota final</strong> — média das notas finais</li>
                    <li><strong>Nota intermediária</strong> — média das notas intermediárias</li>
                    <li><strong>Nota prova final</strong> — média das provas finais</li>
                    <li><strong>Média da instituição</strong> — referência para comparação de notas</li>
                  </ul>
                </div>

                <div className={styles.variableCategory}>
                  <h4 className={styles.variableCategoryTitle}>💰 Dados Financeiros</h4>
                  <ul className={styles.variableList}>
                    <li><strong>Parcelas totais</strong> — quantidade de mensalidades no período</li>
                    <li><strong>Parcelas vencidas</strong> — mensalidades não pagas após vencimento</li>
                    <li><strong>Valor em atraso</strong> — soma dos valores não pagos</li>
                  </ul>
                </div>

                <div className={styles.privacyNote}>
                  <span className={styles.privacyIcon}>🔒</span>
                  <div className={styles.privacyContent}>
                    <h4 className={styles.privacyTitle}>Privacidade e Segurança</h4>
                    <p className={styles.privacyText}>
                      Todos os dados são processados de forma segura e utilizados exclusivamente para
                      identificação de risco de evasão. <strong>Nenhum dado pessoal</strong> é compartilhado
                      ou utilizado para outros fins.
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles.faqSection}>
                <h3 className={styles.faqTitle}>Perguntas Frequentes</h3>
                <div className={styles.faqGrid}>
                  <div className={styles.faqItem}>
                    <h4 className={styles.faqQuestion}>O que acontece se faltam dados?</h4>
                    <p className={styles.faqAnswer}>
                      Se um aluno não tem dados de frequência, nota ou financeiro, o critério correspondente
                      é simplesmente ignorado. O aluno ainda pode ser classificado pelos outros critérios disponíveis.
                    </p>
                  </div>

                  <div className={styles.faqItem}>
                    <h4 className={styles.faqQuestion}>Como interpretar o score de risco?</h4>
                    <p className={styles.faqAnswer}>
                      Cada critério soma pontos conforme a gravidade, numa <strong>escala de 0 a 10</strong>, e a
                      lista é ordenada do maior score para o menor.<br />
                      <strong>1–2 pontos:</strong> Atenção recomendada.<br />
                      <strong>3–5 pontos:</strong> Risco moderado — acompanhamento necessário.<br />
                      <strong>6+ pontos:</strong> Risco alto — ação imediata recomendada.<br />
                      A coluna "Prob. de evasão" mostra o percentual de alunos <em>com o mesmo score</em> que de fato
                      evadiram no último ciclo de validação (janela de um semestre, 180 dias). Quando poucos alunos têm aquele score exato,
                      usamos a taxa da faixa (Alto/Médio/Baixo), que é mais estável. É sempre uma taxa histórica do
                      grupo, não um cálculo individual do aluno, e fica disponível após o primeiro ciclo completo.
                    </p>
                  </div>

                  <div className={styles.faqItem}>
                    <h4 className={styles.faqQuestion}>O que conta como evasão na validação?</h4>
                    <p className={styles.faqAnswer}>
                      Seis meses após cada análise, o sistema verifica a situação dos alunos avaliados.
                      Conta como evasão quem <strong>cancelou, trancou, desistiu ou sumiu dos cadastros</strong> da
                      instituição. Quem <strong>se formou, concluiu o curso ou foi transferido não conta</strong> —
                      nem quem está sem situação informada. Para aplicar essa régua, cada importação registra a
                      situação do aluno naquele dia, com o texto original do sistema da instituição; por isso vale
                      informar "Formado" ou "Trancado" em vez de apenas "Inativo".
                    </p>
                  </div>

                  <div className={styles.faqItem}>
                    <h4 className={styles.faqQuestion}>Qual a diferença para a análise de inadimplência?</h4>
                    <p className={styles.faqAnswer}>
                      A análise de inadimplência usa <strong>Machine Learning</strong> para prever atrasos em pagamentos.
                      A análise de evasão usa <strong>regras determinísticas</strong> para identificar alunos com risco
                      de abandonar a instituição, considerando fatores acadêmicos e financeiros.
                    </p>
                  </div>

                  <div className={styles.faqItem}>
                    <h4 className={styles.faqQuestion}>O que significam os "motivos" que aparecem?</h4>
                    <p className={styles.faqAnswer}>
                      São os critérios específicos que cada aluno atende, como frequência baixa, notas ruins ou atraso
                      em pagamentos. Cada motivo é gerado automaticamente com base nos dados do aluno.
                    </p>
                  </div>

                  <div className={styles.faqItem}>
                    <h4 className={styles.faqQuestion}>Posso exportar?</h4>
                    <p className={styles.faqAnswer}>
                      Sim, é possível exportar a lista em formato CSV.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={styles.fullScreenFooter}>
              <button className={styles.fullScreenCloseButtonFooter} onClick={handleCloseInfoModal}>
                Voltar para a Análise
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para exibir motivos detalhados de evasão */}
      {modalOpen && selectedStudentReasons && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                Motivos de Evasão - Aluno {selectedStudentReasons.studentId}
              </h3>
              <button className={styles.modalCloseButton} onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              {/* Dados que saíram da tabela: visíveis só aqui, no detalhe do aluno */}
              {selectedStudentReasons.student && (
                <div className={`${styles.factorItem} ${styles.factorItemContext}`}>
                  <div className={styles.factorHeader}>
                    <span className={styles.factorName}>Dados do aluno no período</span>
                  </div>
                  <div className={styles.factorValuesRow}>
                    <div className={styles.factorValueItem}>
                      <span className={styles.factorValueLabel}>Frequência</span>
                      <span className={styles.factorValueRef}>{selectedStudentReasons.student.avgFrequency != null ? `${Number(selectedStudentReasons.student.avgFrequency).toFixed(1)}%` : '-'}</span>
                    </div>
                    <div className={styles.factorValueItem}>
                      <span className={styles.factorValueLabel}>Nota Final</span>
                      <span className={styles.factorValueRef}>{selectedStudentReasons.student.avgFinalGrade != null ? Number(selectedStudentReasons.student.avgFinalGrade).toFixed(1) : '-'}</span>
                    </div>
                    <div className={styles.factorValueItem}>
                      <span className={styles.factorValueLabel}>Nota Intermediária</span>
                      <span className={styles.factorValueRef}>{selectedStudentReasons.student.avgIntermediateGrade != null ? Number(selectedStudentReasons.student.avgIntermediateGrade).toFixed(1) : '-'}</span>
                    </div>
                    <div className={styles.factorValueItem}>
                      <span className={styles.factorValueLabel}>Parcelas Vencidas</span>
                      <span className={styles.factorValueRef}>{selectedStudentReasons.student.overdueInstallments != null ? selectedStudentReasons.student.overdueInstallments : '-'}</span>
                    </div>
                    <div className={styles.factorValueItem}>
                      <span className={styles.factorValueLabel}>Valor em Atraso</span>
                      <span className={styles.factorValueRef}>{selectedStudentReasons.student.overdueValue != null ? formatCurrency(selectedStudentReasons.student.overdueValue) : '-'}</span>
                    </div>
                  </div>
                </div>
              )}
              {selectedStudentReasons.factors.length > 0 ? (
                <div className={styles.factorsList}>
                  {selectedStudentReasons.factors.map((factor, index) => (
                    <div key={index} className={styles.factorItem}>
                      <div className={styles.factorHeader}>
                        <span className={styles.factorName}>{factor.friendlyName}</span>
                      </div>
                      <div className={styles.factorDescription}>
                        {factor.description}
                      </div>
                      <div className={styles.factorValuesRow}>
                        <div className={styles.factorValueItem}>
                          <span className={styles.factorValueLabel}>Aluno</span>
                          <span className={styles.factorValueMain}>{factor.value}</span>
                        </div>
                        {factor.formattedAvg && (
                          <div className={styles.factorValueItem}>
                            <span className={styles.factorValueLabel}>{factor.avgLabel || 'Média da instituição'}</span>
                            <span className={styles.factorValueRef}>{factor.formattedAvg}</span>
                          </div>
                        )}
                      </div>
                      {factor.comparisonText && (
                        <div className={styles.factorComparison}>
                          {factor.comparisonText}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.noFactors}>Nenhum motivo de evasão disponível.</p>
              )}

              {/* motivos exatamente como a analise registrou (ReasonsJson) */}
              {selectedStudentReasons.rawReasons?.length > 0 && (
                <div className={styles.factorItem}>
                  <div className={styles.factorHeader}>
                    <span className={styles.factorName}>Motivos registrados na análise</span>
                  </div>
                  <ul className={styles.rawReasonsList}>
                    {selectedStudentReasons.rawReasons.map((reason, index) => (
                      <li key={index}>{reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalCloseButtonFooter} onClick={handleCloseModal}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropoutRisk;
